const _ = require("lodash");
const moment = require("moment");
const uuidv4 = require("uuid").v4;
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const TimeFormat = require("../../../shared/TimeFormat");
const GradeChapter = require("../../../shared/GradeChapter");
const ActivityAux = require("../../../shared/ActivityAux");
const AT = require("./ActionTypes");
const Derived = require("./Derived");
const DerivedBackend = require("./DerivedBackend");
const OpsTimer = require("./OpsTimer");
const OpsTutorial = require("./OpsTutorial");
const ChapterPathUtil = require("../util/ChapterPathUtil");

const {
	atTimeTracking,
	atPage,
	atCurrChapter,
	atCurrBlock,
	atCurrLabBlock,
	atCurrResult,
	atCurrProblem,
	atTutorialCurrentPartName,
	atLeaderboards,
	atChapterPath,
} = require("./StateCursors");

/**
 * Implements the activity tracking on the frontend, which get periodically
 * sent to the user:updateActivityTimes endpoint. This is mostly split off
 * from the original OpsActivity file.
 *
 * Three important differences from the original system:
 * - This file can save unfinished activity objects. The important fields here
 *   are complete, newAmount, and amount. complete is true or false to indicate
 *   which activities are finished. amount is the total time of that activity,
 *   but newAmount just includes the portion of time since the last time the
 *   activity was sent to the server. newAmount and amount are equal if the
 *   activity was never sent to the server unfinished.
 * - The endpoint is idempotent. Attempting to save the same activity twice
 *   will not double count the time. To do this, the frontend generates a uuid,
 *   named tabID here, and assigns a tabSeq of N to the Nth activity recorded
 *   with that tabID. The backend remembers the largest tabSeq that was
 *   successfully saved for each tabID, and any activity without a larger
 *   tabSeq value is ignored as already saved.
 * - This file also handles time tracking in other fields, like
 *   result.timeSpent. It does so in combination with ActivityAux, which takes
 *   activity objects and returns which other timers should be updated by them.
 *
 * Here are some notes about when certain timers should update. (TODO: organize
 * into a readme or doc at some point? Non-devs may want these notes.)
 * - result.timeSpent, implemented as the "result" aux, includes all time on
 *   incomplete results. Viewing solutions or instructions counts. It does not
 *   include any time spent on a completed or intervened result, and it does
 *   not include time with a library or theater modal open.
 * - "Played" time as shown on the user profile, implemented as the "user" aux
 *   with space "trainer", includes all time on the trainer pages without a
 *   library or theater modal open. This includes looking at completed results
 *   and the chapter page where no result is being played.
 * - "Puzzled" time as shown on the user profile, implemented as the "user" aux
 *   with space "lab", is just like "Played" but for lab results.
 * - "Read" time as shown on the user profile, implemented as the "user" aux
 *   with space "library", includes all time on the library pages as well as
 *   time in the trainer or lab with a library modal open.
 * - "Watched" time as shown on the user profile, implemented as the "user" aux
 *   with space "theater", includes all time on the theater pages as well as
 *   time in the trainer or lab with a theater modal open.
 * - In all cases, time is not tracked when the user is idle. Idle is either
 *   switching away from the tab or having no mouse/keyboard input for long
 *   enough. How much is long enough depends on the activity; see
 *   getIdleTimeForActivity here.
 */

/**
 * Maximum number of activity objects to hold onto before dropping some as
 * lost, in case saves are failing for awhile.
 */
const MAX_ACTIVITY_TO_REMEMBER = 100;

/**
 * Maximum number of activity objects to save in one API request. Always saves
 * the earliest ones still remembered. (Saving more recent ones first would
 * cause the tabID/tabSeq system to reject earlier ones.)
 */
const MAX_ACTIVITY_IN_BATCH = 20;

/**
 * When saving partially done activity, skip it if the total time is less than
 * this amount.
 */
const PARTIAL_DONE_MIN_TIME_MS = 10000;

/**
 * Immediately calls the updateActivityTimes endpoint. Used as the throttler
 * implementation.
 */
const saveActivityNow = function(tt, api, throttler, saveAll) {
	const activityList = saveAll
		? tt.doneList
		: tt.doneList.slice(0, MAX_ACTIVITY_IN_BATCH);
	if (activityList.length) {
		api.saveActivityTimes(activityList);
	}
	throttler.cancel("activityTimes", tt);
};

/**
 * Sets up throttler for calling updateActivityTimes in the background.
 */
const setupThrottlerInVault = function(vault) {
	const {api, throttler} = vault;

	throttler.config("activityTimes", {
		delay: 20000,
		run: tt => saveActivityNow(tt, api, throttler),
	});
};

/**
 * Initializes the time tracking object in the state.
 */
const init = function(origTT) {
	const tt = {
		tabID: uuidv4(),
		tabSeq: 1,
		doneList: [],
		nowActivity: null,
		timer: OpsTimer.initPaused(),
		amountAtLastPartial: 0,
		amountAtLastPropagate: 0,
	};
	return tt;
};

/**
 * Called when a save is successful. This removes the saved tabSeq values from
 * our queue. We still go through the trouble of ensuring idempotence in case a
 * network glitch prevents us from hearing from the server that a save
 * succeeded.
 */
const handleSaveSuccess = function(tt, payload) {
	const {activityList} = payload;
	const savedSeqSet = new Set(
		activityList.filter(act => act.tabID === tt.tabID).map(act => act.tabSeq)
	);
	tt = setIn(tt, "doneList", dl =>
		dl.filter(act => !savedSeqSet.has(act.tabSeq))
	);
	return tt;
};

/**
 * Called when a save fails. We simply try again and rely on idempotence to
 * avoid data issues.
 */
const handleSaveFail = function(tt, payload, error, throttler) {
	throttler.queue("activityTimes", tt);
	return tt;
};

/**
 * getCurrentActivity helper for setting fields like type on an activity that
 * is playing a result. Returns an immutably modified activity.
 */
const setActivityResultFields = function(activity, page, result, problem) {
	if (!result || !page) {
		return null;
	}
	const ret = _.clone(activity);
	ret.objectID = result.resultID;
	if (page.problemIndex === "summary") {
		ret.type = "summary";
	} else if (page.problemIndex === "overview") {
		ret.type = "overview";
	} else {
		if (problem) {
			ret.subobjectID = problem.problemID;
		}
		if (page.showInstructions) {
			ret.type = "instructions";
		} else {
			// TODO: Currently using outcome to determine solution or problem
			// page. May need to vary for result type.
			ret.type = problem && problem.outcome ? "solution" : "problem";
		}
	}
	// Below are cases where we don't want to increment result.timeSpent, so we
	// intentionally overwrite the type field if it was set. The condition for
	// result.timeSpent incrementing is in ActivityAux.useResultAux.
	if (page.problemIndex === "summary") {
		// Already set type; avoid the other if branches here.
	} else if (result.isComplete) {
		ret.type = "completed";
	} else if (result.hasIntervened) {
		ret.type = "intervened";
	}
	return ret;
};

/**
 * Internal helper. Given the current vault state, get the current activity
 * object that should be running a timer right now, or null if no times should
 * be stored. This function is the most important determiner of what user
 * actions log to what timers.
 *
 * Note this returns an activity in the in-progress form. See getDoneActivity
 * for the one that returns a form ready to send to the backend.
 */
const getCurrentActivity = function(state) {
	if (state.idle) {
		return null;
	}
	const page = atPage(state).get();
	if (!page || !page.name || page.demo) {
		return null;
	}
	const pageParts = page.name.split(".");
	let newCurrent = {
		section: pageParts[0] || null,
		subsection: pageParts[1] || null,
		type: null,
		objectID: null,
		subobjectID: null,
		data: null,
	};

	// Special case: theater or library modals should count as time spent in
	// theater or library.
	if (Derived.isViewingLibrary(state) && newCurrent.section !== "library") {
		newCurrent.section = "library";
		newCurrent.subsection = "chapter";
		if (page.name === "trainer.problem") {
			newCurrent.type = "lesson-modal";
		} else if (page.name === "trainer.chapter") {
			// in chapter path
			newCurrent.type = "path-modal";
		}
		newCurrent.data = {page: page.name};
	} else if (
		Derived.isViewingTheater(state) &&
		newCurrent.section !== "theater"
	) {
		newCurrent.section = "theater";
		newCurrent.subsection = "set";
		newCurrent.type = "modal";
	} else if (atLeaderboards(state).getIn("isShowingLeaderboardsModal")) {
		newCurrent.section = "leaderboards";
		newCurrent.subsection = null;
		newCurrent.type = "modal";
	}

	if (newCurrent.section === "trainer" || newCurrent.section === "lab") {
		if (newCurrent.subsection === "devProblem") {
			return null;
		} else if (newCurrent.subsection === "chapter") {
			const currentChapter = atCurrChapter(state).get();
			if (currentChapter) {
				newCurrent.objectID = currentChapter.chapterID;
			}

			if (newCurrent.section === "trainer") {
				// if in chapter path, get additional chapter path information
				const isUpstairs = atChapterPath(state).getIn("isUpstairs");
				newCurrent.type = ChapterPathUtil.getFloorStr(isUpstairs);
			}
		} else if (newCurrent.subsection === "type") {
			newCurrent.objectID = page.typeID;
		} else if (
			newCurrent.subsection === "history" &&
			page.problemIndex === "history"
		) {
			newCurrent.type = "history";
			newCurrent.objectID = atCurrBlock(state).getIn("model.blockID") || null;
		} else if (
			newCurrent.subsection === "problem" ||
			newCurrent.subsection === "history"
		) {
			const result = atCurrResult(state).get();
			const problem = atCurrProblem(state).get();
			newCurrent = setActivityResultFields(newCurrent, page, result, problem);
			if (!newCurrent) {
				return null;
			}
		}
	} else if (newCurrent.section === "tutorial") {
		const tutorialPart = atTutorialCurrentPartName(state).get();
		if (OpsTutorial.isDoingResult(tutorialPart)) {
			newCurrent.subsection = "problem";
			const result = atCurrResult(state).get();
			const problem = atCurrProblem(state).get();
			newCurrent = setActivityResultFields(newCurrent, page, result, problem);
			if (!newCurrent) {
				return null;
			}
		}
	} else if (newCurrent.section === "profile") {
		newCurrent.type = page.currentTab;
	} else if (newCurrent.section === "library") {
		// Note: the type may have already been set to "modal" above, so be careful
		// about overwriting the type field in this branch.
		// Collapsed Library+Theater Videos tab keeps page.name library.grade but
		// should attribute time like theater.chapter (legacy theater grid).
		if (page.tab === "videos") {
			newCurrent.section = "theater";
			newCurrent.subsection = "chapter";
			newCurrent.objectID = GradeChapter.toChapterID(
				page.gradeNumber,
				page.chapterIndex
			);
		} else if (newCurrent.subsection === "grade") {
			if (page.gradeNumber) {
				newCurrent.objectID = page.gradeNumber;
			}
		} else if (newCurrent.subsection === "chapter") {
			const libraryCurrent = Derived.getLibraryCurrent(state);
			if (libraryCurrent) {
				newCurrent.objectID = libraryCurrent.chapterID;
				newCurrent.subobjectID = libraryCurrent.sectionID;
			} else {
				return null;
			}
		}
	} else if (newCurrent.section === "theater") {
		// Note: the type may have already been set to "modal" above, so be careful
		// about overwriting the type field in this branch.
		const chapterID = GradeChapter.toChapterID(
			page.gradeNumber,
			page.chapterIndex
		);
		if (newCurrent.subsection === "chapter") {
			newCurrent.objectID = chapterID;
		} else if (newCurrent.subsection === "set") {
			const theaterCurrent = Derived.getTheaterCurrent(state);
			if (theaterCurrent) {
				newCurrent.objectID = theaterCurrent.videoSetID;
				newCurrent.subobjectID = theaterCurrent.videoID;
			} else {
				return null;
			}
		}
	} else if (newCurrent.section === "school") {
		// These are the school/teacher report/accounts pages
		newCurrent.subsection = page.space;
		newCurrent.type = page.tab;
		if (page.tab === "schools") {
			newCurrent.objectID = _.get(page, "pageData.schoolID");
		} else if (page.tab === "classes") {
			newCurrent.objectID = _.get(page, "pageData.classID");
		} else {
			newCurrent.objectID =
				_.get(page, "pageData.studentID") || _.get(page, "pageData.userID");
		}
		if (
			page.space === "reports" &&
			(_.get(page, "pageData.classID") || _.get(page, "pageData.studentID"))
		) {
			newCurrent.data = _.omit(page.pageData, ["classID", "studentID"]);
		}
	}

	// Small bandaid partially addressing T27644.
	const isPossibleID = id =>
		id == null ||
		// These bounds are determined by the range of postgres's integer (4 bytes)
		(_.isSafeInteger(id) && id <= 2147483647 && id >= -2147483648);
	if (
		!isPossibleID(newCurrent.objectID) ||
		!isPossibleID(newCurrent.subobjectID)
	) {
		return null;
	}

	return newCurrent;
};

/**
 * Internal helper. Returns whether the activity changed between old and new
 * state. We generally decide this by deep comparing the two returns of
 * getCurrentActivity, but there are some cases like undetected idles where we
 * want to force a new activity object.
 */
const didActivityChange = function(oldState, newState, action) {
	const oldActivity = getCurrentActivity(oldState);
	const newActivity = getCurrentActivity(newState);
	if (!_.isEqual(oldActivity, newActivity)) {
		return true;
	} else if (action.type === AT.NOTIFY_SLEEP) {
		// If an undetected idle just occurred, then the type of activity changed.
		return true;
	} else if (action.type === AT.FINAL) {
		// Mark the current activity as done if the user tried to close the tab.
		return true;
	}
	return false;
};

/**
 * Internal helper. Given an action and the two states passed to an update
 * hook, return whether we have cause to save all pending activity immediately.
 */
const shouldSaveAllNow = function(oldState, newState, action) {
	if (action.type === AT.FINAL) {
		return true;
	}
	return false;
};

/**
 * Internal helper. Returns how long the user can not interact with the page
 * before they are considered idle for activity logging purposes in
 * milliseconds.
 */
const getIdleTimeForActivity = function(state, activity) {
	// Non-academic pages default to a 1 minute idle timeout
	const nonAcademic = [
		"beastbuilder",
		"goals",
		"home",
		"404",
		"parent",
		"profile",
		"settings",
		"teacher",
		"leaderboards",
	];
	if (_.includes(nonAcademic, activity.section)) {
		return 60000;
	}
	if (activity.section === "trainer" || activity.section === "lab") {
		if (activity.subsection === "chapter") {
			// 30s timeout on the chapter page. Don't want total trainer time to be
			// too different from sum of time in blocks.
			return 30000;
		}
		if (
			activity.type === "summary" ||
			activity.type === "completed" ||
			activity.type === "intervened" ||
			activity.type === "overview"
		) {
			return 60000; // 1 minute for the summary page or equivalent cases
		} else if (
			activity.type === "instructions" ||
			activity.type === "solution"
		) {
			return 300000; // 5 minutes for pages with lots of reading
		} else if (activity.type === "problem") {
			const result = atCurrResult(state).get();
			if (
				result.resultType === "challenge" ||
				result.resultType === "puzzle" ||
				result.resultType === "lab"
			) {
				return 600000; // 10 minutes for challenge or puzzle problems
			} else {
				return 300000; // Otherwise 5 minutes for normal problems
			}
		}
	}
	if (activity.section === "tutorial") {
		return 30000;
	}
	if (activity.section === "library") {
		if (activity.subsection === "grade") {
			// 30s timeout for library grade page. Don't want total library time to be
			// too different from sum of time in chapters.
			return 30000;
		}
		if (activity.subsection === "chapter") {
			return 600000; // 10 minutes per book page
		}
	}

	if (activity.section === "theater") {
		if (activity.subsection === "chapter") {
			return 30000; // Matches trainer chapter page.
		}
		if (activity.subsection === "set") {
			return 300000; // 5 minutes per video set (all videos < 5 minutes)
		}
	}

	// Anything else defaults to 30s
	return 30000;
};

/**
 * Internal helper. Given an in-progress activity, like one returned from
 * getCurrentActivity, return it in a form ready to send to the backend. If
 * complete is false, the activity is returned as an unfinished one, and the
 * same activity will be saved later with newAmount < amount until it is
 * eventually finished.
 */
const getDoneActivity = function(tt, complete, loggedAsIdle, clock) {
	if (!tt.nowActivity) {
		return null;
	}
	const fmt = ms => DerivedBackend.msToNearestTenthSeconds(ms || 0);
	const idleTime = loggedAsIdle ? clock.getIdleDuration() : 0;

	const start = OpsTimer.getStartMs(tt.timer, clock);
	const currAmount = OpsTimer.getAmountMs(tt.timer, clock);
	const newAmount = currAmount - tt.amountAtLastPartial;
	if (!complete && newAmount < PARTIAL_DONE_MIN_TIME_MS) {
		return null;
	}
	const doneActivity = _.extend({}, tt.nowActivity, {
		tabID: tt.tabID,
		tabSeq: tt.tabSeq,
		start: TimeFormat.getDateTimeString(moment(start)),
		amount: fmt(currAmount),
		newAmount: fmt(newAmount),
		complete: !!complete,
		endIdleTime: fmt(idleTime || 0),
	});
	return doneActivity;
};

/**
 * Internal helper. Returns a new value for tt.doneList, with a new activity
 * appended and both pruning or confidence checking taken care of.
 */
const appendDoneActivity = function(tt, newActivity) {
	if (!newActivity) {
		return tt.doneList;
	}
	// Confidence check for T27644: first activity needs to last a certain amount of
	// time to be logged.
	if (newActivity.tabSeq === 1 && newActivity.amount < 5) {
		return tt.doneList;
	}
	return tt.doneList.concat([newActivity]).slice(-MAX_ACTIVITY_TO_REMEMBER);
};

/**
 * Internal helper. Modifies the time tracking object to queue the current
 * activity for saving in finished form, then replaces it with the next
 * activity. Note this next activity could be null if the timing should stop
 * briefly.
 */
const switchToNewActivity = function(tt, newActivity, loggedAsIdle, clock) {
	const doneActivity = getDoneActivity(tt, true, loggedAsIdle, clock);
	const newTabSeq = tt.tabSeq + (doneActivity ? 1 : 0);
	const newDoneList = appendDoneActivity(tt, doneActivity);
	const newTimer = newActivity ? OpsTimer.init(clock) : OpsTimer.initPaused();

	tt = extendIn(tt, [], {
		tabSeq: newTabSeq,
		doneList: newDoneList,
		nowActivity: newActivity,
		timer: newTimer,
		amountAtLastPartial: 0,
		amountAtLastPropagate: 0,
	});
	return tt;
};

/**
 * Internal helper. Modifies the time tracking object to queue the current
 * activity for saving in unfinished form if it has enough time.
 */
const updateActivity = function(tt, clock) {
	tt = setIn(tt, "timer", timer => OpsTimer.updateIfPastIdle(timer, clock));
	const partialActivity = getDoneActivity(tt, false, false, clock);
	if (partialActivity) {
		const newDoneList = appendDoneActivity(tt, partialActivity);
		tt = extendIn(tt, [], {
			tabSeq: tt.tabSeq + 1,
			doneList: newDoneList,
			timer: OpsTimer.update(tt.timer, clock),
			amountAtLastPartial: OpsTimer.getAmountMs(tt.timer, clock),
		});
	}
	return tt;
};

/**
 * Propagates time updates to some frontend fields that need it, like results
 * and problems. In particular we operate on the whole vault state here.
 *
 * Results, problems, etc. store just a timeSpent field that we can increment
 * within this function. This avoids having to do timer management anywhere
 * else in the code.
 */
const propagateActivityToState = function(state, clock) {
	const tt = atTimeTracking(state).get();
	const nowActivity = tt && tt.nowActivity;
	if (!nowActivity || !tt.timer) {
		return state;
	}

	const lastAmount = tt.amountAtLastPropagate;
	const currAmount = OpsTimer.getAmountMs(tt.timer, clock);
	const diff = currAmount - lastAmount;

	if (diff > 0 && ActivityAux.useResultAux(nowActivity)) {
		const sourceType = atCurrResult(state).getIn(["source", "type"]);
		const isLab = sourceType === "lab";

		const problemTSCR = atCurrProblem(state).in("timeSpent");
		if (problemTSCR.exists()) {
			state = problemTSCR.set(ts => ts + diff);
		}
		const resultTSCR = atCurrResult(state).in("timeSpent");
		if (resultTSCR.exists()) {
			state = resultTSCR.set(ts => ts + diff);
		}
		const blockCR = isLab ? atCurrLabBlock(state) : atCurrBlock(state);
		const blockTSCR = blockCR.in("totalTimeSpent");
		if (blockTSCR.exists()) {
			state = blockTSCR.set(ts => ts + diff);
		}
		// We could also update chapter totalTimeSpent or user totalTimeSpent, but
		// it doesn't seem anything on the frontend uses them.
	}
	state = atTimeTracking(state).setIn("amountAtLastPropagate", currAmount);
	return state;
};

/**
 * The main export, a post update hook that does all the necessary maintenance
 * on time tracking, like deciding whether a new activity should start.
 */
const updateTimeTracking = function(vault, oldState, newState, action) {
	const {clock, api, throttler} = vault;
	if (!atTimeTracking(newState).exists()) {
		// Do nothing
	} else if (didActivityChange(oldState, newState, action)) {
		newState = propagateActivityToState(newState, clock);

		const newActivity = getCurrentActivity(newState);
		if (newActivity) {
			clock.setIdleDuration(getIdleTimeForActivity(newState, newActivity));
		}

		const saveAllNow = shouldSaveAllNow(oldState, newState, action);
		const loggedAsIdle = action.type === AT.START_IDLE_OR_BLUR && action.isIdle;
		newState = atTimeTracking(newState).set(tt => {
			tt = switchToNewActivity(tt, newActivity, loggedAsIdle, clock);
			if (saveAllNow) {
				saveActivityNow(tt, api, throttler, true);
			} else {
				throttler.queue("activityTimes", tt);
			}
			return tt;
		});
	} else {
		newState = propagateActivityToState(newState, clock);

		newState = atTimeTracking(newState).set(tt => {
			tt = updateActivity(tt, clock);
			// We do want this save happening frequently to avoid losing data, so
			// queue one up if anything is in progress.
			if (tt.nowActivity || tt.doneList.length) {
				throttler.queue("activityTimes", tt);
			}
			return tt;
		});
	}
	return newState;
};

/**
 * A debugging aid that adds a certain amount of time to the timer.
 */
const debugAddTime = function(state, amountMs) {
	if (atTimeTracking(state).getIn("nowActivity")) {
		state = atTimeTracking(state).setIn(
			"timer",
			at => at && OpsTimer.addToAmount(at, amountMs)
		);
	}
	return state;
};

module.exports = {
	saveActivityNow,
	setupThrottlerInVault,
	init,
	handleSaveSuccess,
	handleSaveFail,
	propagateActivityToState,
	updateTimeTracking,
	debugAddTime,
};
